import Fuse from 'fuse.js';
import {ITraveler} from '../data/interfaces';
import {calculateAdjustedDeadline} from './adjustedDeadline';

const FUSE_SEARCH_OPTIONS = {
  keys: ['searchableText'],
  threshold: 0.2,
  ignoreLocation: true,
};

interface SearchableTraveler {
  traveler: ITraveler;
  searchableText: string;
}

function normalizeSearchText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function getSearchableTravelerTexts(traveler: ITraveler): string[] {
  return [
    traveler.displayName,
    traveler.status,
    traveler.itemTypeString ?? '',
    ...getDeadlineSearchTexts(traveler),
  ];
}

function getDeadlineSearchTexts(traveler: ITraveler): string[] {
  const adjustedDeadline = calculateAdjustedDeadline(traveler.readyToShipDeadline);
  if (!adjustedDeadline) {
    return [];
  }

  const monthName = adjustedDeadline.toLocaleDateString('en-US', {month: 'long'});
  const day = adjustedDeadline.getDate();
  const year = adjustedDeadline.getFullYear();

  return [
    `${monthName} ${day} ${year}`,
    `${monthName} ${day}`,
  ];
}

function createSearchableTraveler(traveler: ITraveler): SearchableTraveler {
  return {
    traveler,
    searchableText: normalizeSearchText(getSearchableTravelerTexts(traveler).join(' ')),
  };
}

function createFuseSearch(searchableTravelers: SearchableTraveler[]): Fuse<SearchableTraveler> {
  return new Fuse(searchableTravelers, FUSE_SEARCH_OPTIONS);
}

function isNumericSearchToken(text: string): boolean {
  return /^\d+$/.test(text);
}

function doesNumericTermMatchToken(queryTerm: string, targetToken: string): boolean {
  if (!isNumericSearchToken(targetToken)) {
    return false;
  }

  if (queryTerm.length <= 2) {
    return targetToken.length <= 3 && targetToken.startsWith(queryTerm);
  }

  return targetToken.includes(queryTerm);
}

function doesStrictTermMatchToken(queryTerm: string, targetToken: string): boolean {
  if (isNumericSearchToken(queryTerm)) {
    return doesNumericTermMatchToken(queryTerm, targetToken);
  }

  if (queryTerm.length <= 2) {
    return targetToken.startsWith(queryTerm);
  }

  return targetToken === queryTerm;
}

function hasStrictTermMatch(searchableTraveler: SearchableTraveler, queryTerm: string): boolean {
  return searchableTraveler.searchableText
    .split(' ')
    .some((targetToken) => doesStrictTermMatchToken(queryTerm, targetToken));
}

function canAcceptFuseTermMatch(queryTerm: string, searchableText: string): boolean {
  return searchableText.split(' ').some((targetToken) => {
    if (isNumericSearchToken(targetToken)) {
      return false;
    }

    if ((queryTerm.length <= 5 || targetToken.length <= 5) && queryTerm.slice(0, 2) !== targetToken.slice(0, 2)) {
      return false;
    }

    return queryTerm[0] === targetToken[0];
  });
}

function getFuseMatches(
  fuseSearch: Fuse<SearchableTraveler>,
  normalizedQuery: string
): Set<ITraveler> {
  return new Set(
    fuseSearch
      .search(normalizedQuery)
      .filter((result) => canAcceptFuseTermMatch(normalizedQuery, result.item.searchableText))
      .map((result) => result.item.traveler)
  );
}

function getTermMatches(
  searchableTravelers: SearchableTraveler[],
  fuseSearch: Fuse<SearchableTraveler>,
  queryTerm: string
): Set<ITraveler> {
  const strictMatches = new Set(
    searchableTravelers
      .filter((searchableTraveler) => hasStrictTermMatch(searchableTraveler, queryTerm))
      .map((searchableTraveler) => searchableTraveler.traveler)
  );

  if (isNumericSearchToken(queryTerm)) {
    return strictMatches;
  }

  return new Set([
    ...strictMatches,
    ...getFuseMatches(fuseSearch, queryTerm),
  ]);
}

export function filterUnscheduledTravelersBySearch(
  travelers: ITraveler[],
  query: string
): ITraveler[] {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) {
    return travelers;
  }

  const searchableTravelers = travelers.map(createSearchableTraveler);
  const fuseSearch = createFuseSearch(searchableTravelers);
  const queryTerms = normalizedQuery.split(' ');
  const termMatches = queryTerms.map((queryTerm) =>
    getTermMatches(searchableTravelers, fuseSearch, queryTerm)
  );

  return travelers.filter((traveler) =>
    termMatches.every((matches) => matches.has(traveler))
  );
}
